import { isBefore } from 'date-fns';
import { computed, makeObservable, observable, when } from 'mobx';
import { rootStore } from 'src/app';
import { DataType, IDeliverable, ItemStatus, UserRole } from '../types';
import { Item, Question, User } from './';
import { computedFn } from 'mobx-utils';


export class Deliverable extends Item<IDeliverable> implements IDeliverable {
  delay_reason?: string  = null;
  delay_date?:  Date    = null;
  delivered:     boolean = null;
  done:          boolean = null;
  to?:           number  = null;
  start_date?:   Date    = null;
  processed:     boolean = null;
  progress:      number  = 0;
  protected constructor(data: IDeliverable) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Deliverable, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually 
    makeObservable(this, {
      delay_reason: observable,
      delay_date:   observable,
      delivered:    observable,
      done:         observable,
      to:           observable,
      start_date:   observable,
      processed:    observable,
      progress:     observable,

      isRelevantForMeeting: computed,
      getTo:                computed,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here. 
    this.fromData(data);
  }

    /**
   * @return <true> if the currently logged in user has the rights to edit this item
   */

  public get canEdit() : boolean {
    return (!this.isNew && !this.archived);
  }
    
  public canEditField = computedFn((field?: keyof IDeliverable): boolean => {
    return !this.isNew && !this.archived;
  })

  public static create(data?: IDeliverable): Deliverable {
    return new Deliverable(data || {
      ...Item.create(data).toData(),
      start_date: new Date(),
      to:         rootStore.authStore.getCurUser.getRepresentative(rootStore.authStore.getCurSpaceID),
      delivered:  false,
      done:       false,
    });
  }

  protected fromData(data: IDeliverable): void {
    super.fromData(data);
    this.delay_reason = data.delay_reason;
    const delay_date = data.delay_date ? new Date(data.delay_date).toISOString() : null;
    this.delay_date   = data.delay_date && new Date(delay_date);

    this.delivered    = data.delivered;
    this.done         = data.done;
    this.to           = data.to && Number(data.to);
    
    const start_date = data.start_date ? new Date(data.start_date).toISOString() : null;
    this.start_date   = data.start_date && new Date(start_date);

    this.processed    = data.processed;
    this.progress     = Number(data.progress);
  }

  public toData(): any {
    return {
      ...super.toData(),
      delay_reason: this.delay_reason,
      delay_date:   this.delay_date && this.delay_date.toISOString(),
      delivered:    this.delivered,
      done:         this.done,
      to:           this.to,
      start_date:   this.start_date && this.start_date.toISOString(),
      processed:    this.processed,
      progress:     this.progress,
    };
  }

  public toPatchData(patch: Partial<IDeliverable>): any {
    return {
      ...super.toPatchData(patch),
      delay_reason: "delay_reason" in patch ? patch.delay_reason : undefined,
      delay_date:   "delay_date" in patch ? patch.delay_date && patch.delay_date.toISOString() : undefined,
      delivered:    "delivered" in patch ? patch.delivered : undefined,
      done:         "done" in patch ? patch.done : undefined,
      to:           "to" in patch ? patch.to : undefined,
      start_date:   "start_date" in patch ? patch.start_date && patch.start_date.toISOString() : undefined,
      processed:    "processed" in patch ? patch.processed : undefined,
      progress:     "progress" in patch ? patch.progress : undefined,
    };
  }

  get getStatus() {
    if(!this.id) return ItemStatus.New;
    if(this.delivered) return ItemStatus.Answered;
    return ItemStatus.Active;
  }

  get getTo(): User | null {
    return this.to ? rootStore.userStore.container[this.to] : null;
  }
  
  get isRelevantForMeeting(): boolean {
    return !this.done && isBefore(this.date, new Date());
  }

  /**
   * Move deliverable to @room_id . This will unlink all other items and users in the backend. 
   * 
   * @param room_id  target room within the current project the user has access to 
   */
  remit = async (room_id: number): Promise<boolean> => {
    try {
      await this.set("room_id", room_id, true);
      return true;
    } catch (err) {
      console.error("Failed to relocate deliverable", err);
      return false;
    }
  }

  /**
   * Create a new question and set it as active question. When that question
   * is saved, it is automatically linked to @this 
   */
  linkNewQuestion = () => {
    rootStore.questionStore.setActive(Question.create());
    const new_question = rootStore.questionStore.getActive;
    when(() => !new_question.isNew, () => rootStore.delivStore.addItemLink(new_question.id, DataType.Question));
  }

  public validate = async (asBoolean?: boolean): Promise<any> => {
    let parentInvalids = await super.validate();
    if(this.isNew) return [];
    if(this.done)  return [];

    // type this object, make generic for items and let each items contribute to list of required fields
    const obj: any = {
      to: this.to,
      start_date: this.start_date,
    };
    
    const itemInvalids = Object.keys(obj).filter((k) => {
      return !obj[k];
    });

    const invalids = [...parentInvalids, ...itemInvalids]

    return asBoolean ? invalids.length : invalids;
  }
}

export namespace Deliverable {
  export enum Status {
    New = "new",
  }
}